import { WORLD_CHAMPIONSHIP_2026 } from "@/data/internationalTournaments";
import type { ScheduledGame, PeriodScores } from "@/hooks/useSchedule";
import type { ScrapedWorldsGame } from "@/hooks/useWorldsLive";

const ISO: Record<string, string> = {
  FIN: "fi", CZE: "cz", CAN: "ca", USA: "us", SUI: "ch", SWE: "se",
  NOR: "no", LAT: "lv", DEN: "dk", GER: "de", SVK: "sk", AUT: "at",
  SLO: "si", ITA: "it", HUN: "hu", GBR: "gb",
};

const NAMES: Record<string, string> = {
  FIN: "Finland", CZE: "Czechia", CAN: "Canada", USA: "United States",
  SUI: "Switzerland", SWE: "Sweden", NOR: "Norway", LAT: "Latvia",
  DEN: "Denmark", GER: "Germany", SVK: "Slovakia", AUT: "Austria",
  SLO: "Slovenia", ITA: "Italy", HUN: "Hungary", GBR: "Great Britain",
  TBD: "TBD",
};

const MONTHS: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

const EMPTY_PERIODS: PeriodScores = {
  first: { home: null, away: null },
  second: { home: null, away: null },
  third: { home: null, away: null },
  overtime: { home: null, away: null },
  penalties: { home: null, away: null },
};

const WORLDS_BROADCASTS = [
  "TV4 / C More (SE)",
  "MTV3 / C More (FI)",
  "NHL Network / ESPN+ (US)",
  "TSN / RDS (CA)",
  "ČT Sport (CZ)",
  "SRF (CH)",
  "Viaplay (EU)",
];

const TOURNAMENT_YEAR = 2026;

/** Parse "May 15" + "20:20 CET" assuming Swiss local time (CEST in May = UTC+2). */
function parseWorldsDateTime(dateStr?: string, timeStr?: string) {
  if (!dateStr) return null;
  const parts = dateStr.trim().split(/\s+/);
  const mon = parts[0];
  const day = parseInt(parts[1], 10);
  const month = MONTHS[mon as keyof typeof MONTHS];
  if (month === undefined || isNaN(day)) return null;

  let h = 12, m = 0;
  if (timeStr) {
    const tm = timeStr.match(/(\d{1,2}):(\d{2})/);
    if (tm) {
      h = parseInt(tm[1], 10);
      m = parseInt(tm[2], 10);
    }
  }
  // Swiss local time in May = CEST (UTC+2)
  const tsMs = Date.UTC(TOURNAMENT_YEAR, month, day, h - 2, m);
  const localDate = `${TOURNAMENT_YEAR}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return {
    iso: new Date(tsMs).toISOString(),
    timestamp: Math.floor(tsMs / 1000),
    localDate, // Swiss tournament-day grouping
  };
}

function stableId(key: string) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return Math.abs(h) + 900_000_000; // namespace to avoid collisions with API-Sports IDs
}

function flagLogo(code: string) {
  const cc = ISO[code];
  return cc ? `https://flagcdn.com/w80/${cc}.png` : "";
}

interface BuildOpts {
  idKey: string;
  team1: string;
  team2: string;
  score1: number | null;
  score2: number | null;
  dateStr?: string;
  timeStr?: string;
  venue?: string;
  isOvertime?: boolean;
  isShootout?: boolean;
  isUpcoming: boolean;
  roundLabel: string;
}

function buildGame(o: BuildOpts): { game: ScheduledGame; localDate: string } | null {
  const dt = parseWorldsDateTime(o.dateStr, o.timeStr);
  if (!dt) return null;

  const hasScore = !o.isUpcoming && o.score1 != null && o.score2 != null;
  const isFinished = hasScore;
  const isScheduled = !hasScore;
  const statusShort = isFinished
    ? o.isShootout ? "AP" : o.isOvertime ? "AOT" : "FT"
    : "NS";
  const statusLong = isFinished ? "Match Finished" : o.roundLabel;

  const localTime = new Date(dt.iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const game: ScheduledGame = {
    id: stableId(`worlds-${o.idKey}`),
    date: dt.iso,
    time: localTime,
    timestamp: dt.timestamp,
    homeTeam: NAMES[o.team1] ?? o.team1,
    awayTeam: NAMES[o.team2] ?? o.team2,
    homeScore: hasScore ? o.score1 : null,
    awayScore: hasScore ? o.score2 : null,
    status: statusLong,
    statusShort,
    isLive: false,
    isFinished,
    isScheduled,
    homeAbbr: o.team1,
    awayAbbr: o.team2,
    homeLogo: flagLogo(o.team1),
    awayLogo: flagLogo(o.team2),
    league: "Worlds" as ScheduledGame["league"],
    leagueName: "IIHF World Championship 2026",
    leagueLogo: "",
    country: "Switzerland",
    countryFlag: "🇨🇭",
    periods: EMPTY_PERIODS,
    venue: o.venue,
    tvChannels: WORLDS_BROADCASTS,
  };

  return { game, localDate: dt.localDate };
}

/** Return IIHF Worlds 2026 games happening on the given Swiss-local date (YYYY-MM-DD). */
export function getWorldsGamesForDate(date: string): ScheduledGame[] {
  const out: ScheduledGame[] = [];
  const t = WORLD_CHAMPIONSHIP_2026;

  (t.groupStage ?? []).forEach((group) => {
    group.matches.forEach((m, idx) => {
      const built = buildGame({
        idKey: `gp-${group.name.replace(/\W/g, "")}-${idx}`,
        team1: m.team1,
        team2: m.team2,
        score1: m.score1 ?? null,
        score2: m.score2 ?? null,
        dateStr: m.date,
        timeStr: m.time,
        venue: m.venue,
        isOvertime: m.isOvertime,
        isShootout: m.isShootout,
        isUpcoming: m.score1 == null || m.score2 == null,
        roundLabel: group.name,
      });
      if (built && built.localDate === date) out.push(built.game);
    });
  });

  t.knockoutRounds.forEach((round) => {
    round.matchups.forEach((m) => {
      const built = buildGame({
        idKey: `ko-${m.id}`,
        team1: m.team1,
        team2: m.team2,
        score1: m.score1,
        score2: m.score2,
        dateStr: m.date,
        timeStr: m.time,
        venue: m.venue,
        isOvertime: m.isOvertime,
        isShootout: m.isShootout,
        isUpcoming: m.status === "upcoming",
        roundLabel: round.name,
      });
      if (built && built.localDate === date) out.push(built.game);
    });
  });

  return out;
}

const pairKey = (a: string, b: string) => [a, b].sort().join("|");

function scrapedLocalDate(s: ScrapedWorldsGame): string | null {
  const dt = parseWorldsDateTime(s.date, s.timeLocal);
  return dt?.localDate ?? null;
}

/**
 * Mutate a ScheduledGame[] in place by overlaying live scraped IIHF Worlds data:
 * scores, isLive/isFinished/isScheduled, and a short status label.
 */
export function enrichWorldsGames(
  games: ScheduledGame[],
  scraped: ScrapedWorldsGame[] | undefined,
): ScheduledGame[] {
  if (!scraped?.length) return games;

  const byKey = new Map<string, ScrapedWorldsGame>();
  for (const s of scraped) {
    const ld = scrapedLocalDate(s);
    if (!ld) continue;
    byKey.set(`${ld}|${pairKey(s.team1, s.team2)}`, s);
  }

  return games.map((g) => {
    if (g.league !== "Worlds") return g;
    const ld = new Date(g.timestamp * 1000).toISOString().slice(0, 10);
    // Try both the UTC-derived date and the Swiss local date (UTC+2)
    const swissLd = new Date((g.timestamp + 2 * 3600) * 1000).toISOString().slice(0, 10);
    const key1 = `${ld}|${pairKey(g.homeAbbr, g.awayAbbr)}`;
    const key2 = `${swissLd}|${pairKey(g.homeAbbr, g.awayAbbr)}`;
    const s = byKey.get(key1) ?? byKey.get(key2);
    if (!s) return g;

    const swapped = s.team1 === g.awayAbbr;
    const hScore = swapped ? s.score2 : s.score1;
    const aScore = swapped ? s.score1 : s.score2;

    const isLive = s.status === "live";
    const isFinished = s.status === "finished";
    const isScheduled = s.status === "upcoming";

    let statusShort = g.statusShort;
    let status = g.status;
    if (isLive) {
      statusShort = "LIVE";
      status = "Live";
    } else if (isFinished) {
      statusShort = s.isShootout ? "AP" : s.isOvertime ? "AOT" : "FT";
      status = "Match Finished";
    }

    return {
      ...g,
      homeScore: hScore ?? g.homeScore,
      awayScore: aScore ?? g.awayScore,
      isLive,
      isFinished,
      isScheduled,
      statusShort,
      status,
    };
  });
}

